// AI-ASSISTED: generated with claude-code (Opus 4.8) on 2026-09-14, reviewed by Yoann Hertienne
//
// Thin typed client over the backend plugin's /api/releases/* routes
// (contracts/releases-api.md). Uses discoveryApi + fetchApi so every request
// carries the user's Backstage session — the browser never reads changelogs or
// talks to Bitbucket/Jira directly. Mirrors modules/maintenance/
// MaintenanceApiClient.ts.

import type { DiscoveryApi, FetchApi } from '@backstage/core-plugin-api';
import type {
  ComponentHistoryResponse,
  CoverageReport,
  FeedQuery,
  FeedResponse,
} from '../releases.types';

export class ReleasesApiError extends Error {
  constructor(
    public readonly status: number,
    message: string,
  ) {
    super(message);
    this.name = 'ReleasesApiError';
  }
}

export class ReleasesApiClient {
  constructor(
    private readonly deps: { discoveryApi: DiscoveryApi; fetchApi: FetchApi },
  ) {}

  private baseUrl(): Promise<string> {
    return this.deps.discoveryApi.getBaseUrl('releases');
  }

  private async request<T>(path: string): Promise<T> {
    const baseUrl = await this.baseUrl();
    const url = `${baseUrl}${path}`;
    const res = await this.deps.fetchApi.fetch(url);
    if (!res.ok) {
      const body = await res.text().catch(() => '');
      const bodySuffix = body ? `: ${body}` : '';
      throw new ReleasesApiError(res.status, `GET ${path} → ${res.status}${bodySuffix}`);
    }
    return (await res.json()) as T;
  }

  /**
   * Never throws on a disabled feature — used by the sidebar item and the page
   * shell to decide whether to render at all (FR-023).
   */
  async enabled(): Promise<boolean> {
    try {
      const baseUrl = await this.baseUrl();
      const res = await this.deps.fetchApi.fetch(`${baseUrl}/enabled`);
      if (!res.ok) return false;
      const data = (await res.json()) as { enabled?: boolean };
      return Boolean(data.enabled);
    } catch {
      return false;
    }
  }

  /** The estate-wide feed (US1), newest-first, optionally filtered. */
  getFeed(query: FeedQuery = {}): Promise<FeedResponse> {
    const params = new URLSearchParams();
    if (query.team) params.set('team', query.team);
    if (query.component) params.set('component', query.component);
    if (query.limit !== undefined) params.set('limit', String(query.limit));
    const qs = params.toString();
    return this.request<FeedResponse>(`/${qs ? `?${qs}` : ''}`);
  }

  /** One component's full release history (US3), newest-first. */
  getComponentHistory(name: string): Promise<ComponentHistoryResponse> {
    return this.request<ComponentHistoryResponse>(
      `/components/${encodeURIComponent(name)}`,
    );
  }

  /** The CHANGELOG coverage / hygiene snapshot (US4). */
  getCoverage(): Promise<CoverageReport> {
    return this.request<CoverageReport>('/coverage');
  }
}
